/**
 * Cart Drawer - Dawn Theme Pattern
 * Custom Elements for cart functionality
 */

// Constants
const ON_CHANGE_DEBOUNCE_TIMER = 300;

const routes = {
  cart_add_url: '/cart/add',
  cart_change_url: '/cart/change',
  cart_update_url: '/cart/update',
  cart_url: '/cart'
};

// Helper Functions
function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': `application/${type}` }
  };
}

// Focus trap for accessibility
let focusTrap = null;

function trapFocus(container, elementToFocus = container) {
  const elements = Array.from(
    container.querySelectorAll('button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')
  );

  const first = elements[0];
  const last = elements[elements.length - 1];

  removeTrapFocus();

  focusTrap = (event) => {
    if (event.code.toUpperCase() !== 'TAB') return;

    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('keydown', focusTrap);
  elementToFocus.focus();
}

function removeTrapFocus(elementToFocus = null) {
  if (focusTrap) {
    document.removeEventListener('keydown', focusTrap);
    focusTrap = null;
  }
  if (elementToFocus) elementToFocus.focus();
}

// Cart strings for messages
window.cartStrings = window.cartStrings || {
  error: 'There was an error while updating your cart. Please try again.',
  quantityError: 'You can only add [quantity] of this item to your cart.',
  itemRemoved: 'Item removed from cart'
};

// Custom Elements

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', class CartDrawer extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      // Set up event listeners after element is connected to DOM
      this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());

      // Close button clicks
      this.querySelectorAll('[data-cart-close]').forEach(button => {
        button.addEventListener('click', this.close.bind(this));
      });

      // Overlay click
      const overlay = this.querySelector('.cart-drawer__overlay');
      if (overlay) {
        overlay.addEventListener('click', this.close.bind(this));
      }
    }

    open() {
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';

      // Focus management for accessibility
      const content = this.querySelector('.cart-drawer__content');
      if (content) trapFocus(content);
    }

    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';

      removeTrapFocus();
    }

    renderContents(parsedState) {
      const countBubble = this.querySelector('#cart-drawer-count');
      if (countBubble) countBubble.textContent = parsedState.item_count;

      this.classList.toggle('is-empty', parsedState.item_count === 0);

      const cartFooter = document.getElementById('CartDrawer-CartFooter');
      if (cartFooter) {
        cartFooter.style.display = parsedState.item_count === 0 ? 'none' : 'block';
      }

      this.getSectionsToRender().forEach((section => {
        const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
        if (sectionElement && parsedState.sections[section.id]) {
          const newHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
          if (newHTML !== null) {
            sectionElement.innerHTML = newHTML;
          }
        }
      }));

      setTimeout(() => {
        this.open();
      });
    }

    getSectionInnerHTML(html, selector = '.shopify-section') {
      const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
      const element = parsedHTML.querySelector(selector);
      return element ? element.innerHTML : null;
    }

    getSectionsToRender() {
      return [
        {
          id: 'cart-drawer',
          selector: '.cart-drawer__content'
        },
        {
          id: 'cart-icon-bubble'
        }
      ];
    }

    getSectionDOM(html, selector = '.shopify-section') {
      const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
      return parsedHTML.querySelector(selector);
    }
  });
}

if (!customElements.get('cart-drawer-items')) {
  customElements.define('cart-drawer-items', class CartDrawerItems extends HTMLElement {
    constructor() {
      super();

      this.lineItemStatusElement = document.getElementById('CartDrawer-LineItemStatus');

      const debouncedOnChange = debounce((event) => {
        this.onChange(event);
      }, ON_CHANGE_DEBOUNCE_TIMER);

      // Listen for quantity change events from buttons
      this.addEventListener('quantitychange', (event) => {
        const target = event.target;
        if (target.name === 'updates[]') {
          this.updateQuantity(
            target.dataset.index,
            event.detail.newValue,
            document.activeElement.getAttribute('name')
          );
        }
      });

      // Listen for direct input changes
      this.addEventListener('change', debouncedOnChange.bind(this));
    }

    onChange(event) {
      const target = event.target;

      if (target.name === 'updates[]') {
        this.updateQuantity(
          target.dataset.index,
          target.value,
          document.activeElement.getAttribute('name')
        );
      }
    }

    getSectionsToRender() {
      return [
        {
          id: 'cart-drawer-items',
          section: 'cart-drawer',
          selector: '#cart-drawer-items'
        },
        {
          id: 'CartDrawer-CartFooter',
          section: 'cart-drawer',
          selector: '#CartDrawer-CartFooter'
        }
      ];
    }

    updateQuantity(line, quantity, name) {
      this.enableLoading(line);

      const body = JSON.stringify({
        line,
        quantity,
        sections: 'cart-drawer',
        sections_url: window.location.pathname
      });

      fetch(`${routes.cart_change_url}`, {...fetchConfig(), ...{ body }})
        .then((response) => {
          // Handle 422 error (availability issue)
          if (response.status === 422) {
            return response.json().then(data => {
              const errorMessage = data.description || data.message || data.errors || 'Only limited quantity available';

              // Show toast notification
              if (typeof window.showToast === 'function') {
                window.showToast(errorMessage, 'warning');
              }

              // Return the error response as JSON string to be parsed
              return JSON.stringify(data);
            });
          }
          return response.text();
        })
        .then((state) => {
          const parsedState = JSON.parse(state);

          // Check if this was an error response
          if (parsedState.status === 422 || parsedState.errors) {
            this.disableLoading(line);
            // Refresh cart to show actual state
            fetch(window.location.pathname + '?section_id=cart-drawer')
              .then(response => response.text())
              .then(html => {
                const parser = new DOMParser();
                const doc = parser.parseFromString(html, 'text/html');
                const newCartDrawer = doc.querySelector('cart-drawer');
                const currentCartDrawer = document.querySelector('cart-drawer');

                if (newCartDrawer && currentCartDrawer) {
                  const wasOpen = currentCartDrawer.classList.contains('is-open');
                  currentCartDrawer.replaceWith(newCartDrawer);

                  // Update header cart count
                  const cartCount = newCartDrawer.querySelector('#cart-drawer-count');
                  if (typeof window.updateCartCount === 'function') {
                    window.updateCartCount(cartCount ? parseInt(cartCount.textContent) || 0 : 0);
                  }

                  // Reopen drawer if it was open
                  if (wasOpen) {
                    const refreshedCartDrawer = document.querySelector('cart-drawer');
                    if (refreshedCartDrawer && refreshedCartDrawer.open) {
                      refreshedCartDrawer.open();
                    }
                  }
                }
              });
            return; // Exit early, don't continue with normal flow
          }

          // Use Section Rendering API to refresh the cart drawer
          fetch(window.location.pathname + '?section_id=cart-drawer')
            .then(response => response.text())
            .then(html => {
              const parser = new DOMParser();
              const doc = parser.parseFromString(html, 'text/html');
              const newCartDrawer = doc.querySelector('cart-drawer');
              const currentCartDrawer = document.querySelector('cart-drawer');

              if (newCartDrawer && currentCartDrawer) {
                // Replace entire cart drawer to reinitialize custom elements
                const wasOpen = currentCartDrawer.classList.contains('is-open');
                currentCartDrawer.replaceWith(newCartDrawer);

                // Update header cart count
                const cartCount = newCartDrawer.querySelector('#cart-drawer-count');
                if (typeof window.updateCartCount === 'function') {
                  window.updateCartCount(cartCount ? parseInt(cartCount.textContent) || 0 : 0);
                }

                // Reopen drawer if it was open
                if (wasOpen) {
                  const refreshedCartDrawer = document.querySelector('cart-drawer');
                  if (refreshedCartDrawer) {
                    // If cart is empty, close after a delay
                    if (parsedState.item_count === 0) {
                      setTimeout(() => {
                        if (refreshedCartDrawer.close) refreshedCartDrawer.close();
                      }, 1000);
                    } else if (refreshedCartDrawer.open) {
                      refreshedCartDrawer.open();
                    }
                  }
                }
              }

              const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;

              let message = '';
              if (updatedValue === undefined) {
                message = window.cartStrings.itemRemoved;
              } else if (updatedValue !== parseInt(quantity)) {
                message = window.cartStrings.quantityError.replace('[quantity]', quantity);
              }

              this.updateLiveRegions(line, message);
            });
        })
        .catch((error) => {
          console.error('Error:', error);
          this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));
          const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
          if (errors) errors.textContent = window.cartStrings.error;
          this.disableLoading(line);
        });
    }

    updateLiveRegions(line, message) {
      const lineItemError = document.getElementById(`CartDrawer-LineItemError-${line}`) || document.getElementById(`Line-item-error-${line}`);
      if (lineItemError) {
        lineItemError.textContent = message;
        lineItemError.setAttribute('role', message ? 'alert' : '');
      }

      if (this.lineItemStatusElement) {
        this.lineItemStatusElement.textContent = message;
        this.lineItemStatusElement.setAttribute('role', message ? 'status' : '');
      }

      setTimeout(() => {
        if (lineItemError) lineItemError.textContent = '';
        if (this.lineItemStatusElement) this.lineItemStatusElement.textContent = '';
      }, 3000);
    }

    getSectionInnerHTML(html, selector) {
      const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
      const element = parsedHTML.querySelector(selector);
      return element ? element.innerHTML : null;
    }

    enableLoading(line) {
      const cartItems = document.getElementById('cart-drawer-items');
      if (cartItems) cartItems.classList.add('cart__items--disabled');

      const cartItem = document.getElementById(`CartDrawer-Item-${line}`);
      if (cartItem) {
        const buttons = cartItem.querySelectorAll('button, input');
        buttons.forEach((button) => {
          button.disabled = true;
        });
      }
    }

    disableLoading(line) {
      const cartItems = document.getElementById('cart-drawer-items');
      if (cartItems) cartItems.classList.remove('cart__items--disabled');

      const cartItem = document.getElementById(`CartDrawer-Item-${line}`);
      if (cartItem) {
        const buttons = cartItem.querySelectorAll('button, input');
        buttons.forEach((button) => {
          button.disabled = false;
        });
      }
    }
  });
}

if (!customElements.get('cart-note')) {
  customElements.define('cart-note', class CartNote extends HTMLElement {
    constructor() {
      super();

      this.addEventListener('input', debounce((event) => {
        const body = JSON.stringify({ note: event.target.value });
        fetch(`${routes.cart_update_url}`, {...fetchConfig(), ...{ body }});
      }, ON_CHANGE_DEBOUNCE_TIMER));
    }
  });
}

if (!customElements.get('cart-remove-button')) {
  customElements.define('cart-remove-button', class CartRemoveButton extends HTMLElement {
    constructor() {
      super();

      this.addEventListener('click', (event) => {
        event.preventDefault();
        const cartItems = this.closest('cart-drawer-items') || this.closest('cart-items');
        if (cartItems) {
          cartItems.updateQuantity(this.dataset.index, 0);
        }
      });
    }
  });
}

if (!customElements.get('quantity-input')) {
  customElements.define('quantity-input', class QuantityInput extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('input');

      this.querySelectorAll('button').forEach((button) =>
        button.addEventListener('click', this.onButtonClick.bind(this))
      );
    }

    onButtonClick(event) {
      event.preventDefault();
      const currentValue = parseInt(this.input.value) || 1;
      let newValue = currentValue;

      // Calculate new value without updating input
      if (event.target.name === 'plus' || event.target.closest('[name="plus"]')) {
        newValue = currentValue + 1;
      } else {
        newValue = Math.max(0, currentValue - 1);
      }

      // Only trigger update if value changed
      if (newValue !== currentValue) {
        // Create custom event with new value
        const changeEvent = new CustomEvent('quantitychange', {
          bubbles: true,
          detail: { newValue: newValue, oldValue: currentValue }
        });
        this.input.dispatchEvent(changeEvent);
      }
    }
  });
}

// Expose cart drawer open function globally for compatibility
window.openCartDrawer = function(forceUpdate) {
  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer) {
    if (forceUpdate) {
      // Use Section Rendering API to get fresh cart HTML
      fetch(window.location.pathname + '?section_id=cart-drawer')
        .then(response => response.text())
        .then(html => {
          // Parse the HTML response
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');

          // Extract the new cart drawer element
          const newCartDrawer = doc.querySelector('cart-drawer');

          if (newCartDrawer && cartDrawer) {
            // Use replaceWith to properly trigger custom element lifecycle
            const oldCartDrawer = cartDrawer;
            oldCartDrawer.replaceWith(newCartDrawer);

            // Get reference to the new cart drawer
            const refreshedCartDrawer = document.querySelector('cart-drawer');

            // Update cart count in header
            const cartCount = refreshedCartDrawer.querySelector('#cart-drawer-count');
            if (typeof window.updateCartCount === 'function') {
              window.updateCartCount(cartCount ? parseInt(cartCount.textContent) || 0 : 0);
            }

            // Open the new drawer
            if (refreshedCartDrawer && typeof refreshedCartDrawer.open === 'function') {
              setTimeout(() => {
                refreshedCartDrawer.open();
              }, 100);
            }
          }
        })
        .catch(error => {
          console.error('Error fetching cart:', error);
          // Fallback: just open the drawer even if update fails
          cartDrawer.open();
        });
    } else {
      cartDrawer.open();
    }
  }
};

window.closeCartDrawer = function() {
  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer) {
    cartDrawer.close();
  }
};

  /* Release the body scroll-lock when checkout starts.
     The drawer sets body{overflow:hidden} while open, and Razorpay Magic
     Checkout opens over the top of it. In in-app webviews (Instagram in
     particular) the keyboard does not resize the viewport, so the page never
     learns the visible area shrank and cannot scroll a focused input clear of
     the keyboard - a locked body removes the last chance of that working.

     This alone will not fix Razorpay's fixed-position modal; the field sits in
     a fixed container that would not scroll regardless. It removes our
     contribution to the problem. See the Razorpay ticket for the real fix. */
  document.addEventListener('click', function (e) {
    var target = e.target;
    if (!target || typeof target.closest !== 'function') return;
    if (!target.closest('[name="checkout"], .cart__checkout-button, .cart-drawer__checkout-btn')) return;
    document.body.style.overflow = '';
  }, true);
